/**
 * Web implementation of `@react-native-async-storage/async-storage`.
 *
 * localStorage-backed, and that is fine *here specifically*: the only consumer
 * is the react-query persisted cache (src/app/providers.tsx), which is
 * plaintext-safe by design — no faces, and no tokens. The session credential
 * never comes near this file; it lives in an HttpOnly cookie the page cannot
 * read (src/shared/api/session-mode.web.ts). Keep it that way: anything written
 * through here is readable by any script that gets onto the page.
 */
const PREFIX = 'rn-async-storage-web-shim:';

function keyFor(key) {
  return PREFIX + key;
}

async function getItem(key) {
  const value = window.localStorage.getItem(keyFor(key));
  return value === undefined ? null : value;
}

async function setItem(key, value) {
  window.localStorage.setItem(keyFor(key), value);
}

async function removeItem(key) {
  window.localStorage.removeItem(keyFor(key));
}

async function clear() {
  Object.keys(window.localStorage)
    .filter(k => k.startsWith(PREFIX))
    .forEach(k => window.localStorage.removeItem(k));
}

async function getAllKeys() {
  return Object.keys(window.localStorage)
    .filter(k => k.startsWith(PREFIX))
    .map(k => k.slice(PREFIX.length));
}

async function multiGet(keys) {
  return Promise.all(keys.map(async k => [k, await getItem(k)]));
}

async function multiSet(pairs) {
  await Promise.all(pairs.map(([k, v]) => setItem(k, v)));
}

async function multiRemove(keys) {
  await Promise.all(keys.map(k => removeItem(k)));
}

const AsyncStorage = {
  getItem,
  setItem,
  removeItem,
  clear,
  getAllKeys,
  multiGet,
  multiSet,
  multiRemove,
};

export default AsyncStorage;
